import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { eventCallWithThunk } from '@reducers/utils';
import { mergeTimelineEvents } from '@components/machines/components/timeline/helpers/mergeTimelineEvents';
import { SIGNALR_EVENT_NAME, SIGNALR_CONNECTION_NAME, SIGNALR_DEFAULT_TIMEOUT } from '@components/machines/components/timeline/constants';
import type { Events } from '@interfaces/timeline';
import type { RealtimeTimelineEvent } from '@components/machines/components/timeline/types';
import type { GroupedMachineEvent } from '@api/domain/types';
import type { AppThunk } from '@store/types';

type ProcessResponseFn<T> = (machineEvents: GroupedMachineEvent[], from: string, until: string) => T;

interface FetchTimelineWithRealtimeParams<T> {
  machineId?: number | null;
  from?: string;
  until?: string;
  productionBatchId?: number | null;
  thunk: AppThunk<Promise<unknown>>;
  processResponse: ProcessResponseFn<T>;
  onInitialData: (data: T) => void;
  onRealtimeData: (data: T) => void;
  getCurrentEvents: () => Events;
  timeout?: number;
}

const buildMatchEventFunction = ({ machineId, from, until, productionBatchId }: Pick<FetchTimelineWithRealtimeParams<unknown>, 'machineId' | 'from' | 'until' | 'productionBatchId'>) => {
  return (e: unknown) => {
    const evt = e as RealtimeTimelineEvent;
    const request = evt?.request;
    if (!request) return false;

    if (machineId != null && request.machineId !== machineId) return false;
    if (productionBatchId != null && request.productionBatchId !== productionBatchId) return false;
    if (from != null && request.from !== from) return false;
    if (until != null && request.until !== until) return false;

    return true;
  };
};

export const useRealtimeTimelineUpdates = () => {
  const dispatch = useAppDispatch();

  const fetchTimelineWithRealtime = <T>({
    machineId,
    from,
    until,
    productionBatchId,
    thunk,
    processResponse,
    onInitialData,
    onRealtimeData,
    getCurrentEvents,
    timeout = SIGNALR_DEFAULT_TIMEOUT,
  }: FetchTimelineWithRealtimeParams<T>): (() => void) => {
    let cancelled = false;

    dispatch(
      eventCallWithThunk({
        eventName: SIGNALR_EVENT_NAME,
        connectionName: SIGNALR_CONNECTION_NAME,
        thunk,
        thunkHandler: (res) => {
          if (cancelled) return;
          const machineEvents = (res as any)?.machineEvents ?? (res as any)?.events ?? [];
          onInitialData(processResponse(machineEvents, from ?? '', until ?? ''));
        },
        matchEventFunction: buildMatchEventFunction({ machineId, from, until, productionBatchId }),
        timeout,
      })
    )
      .then((realtimeEvent) => {
        if (cancelled) return;
        const evt = realtimeEvent as RealtimeTimelineEvent;
        if (evt?.machineEvents?.length) {
          const current = getCurrentEvents();
          const merged = mergeTimelineEvents({
            existingEvents: current.events,
            newMachineEvents: evt.machineEvents,
            start: from ?? '',
            end: until ?? '',
          });
          onRealtimeData(processResponse(merged.events, from ?? '', until ?? ''));
        }
      })
      .catch((reason) => {
        if (reason !== 'timeout' && reason !== 'error') {
          console.warn('[useRealtimeTimelineUpdates] unexpected rejection:', reason);
        }
      });

    return () => { cancelled = true; };
  };

  return { fetchTimelineWithRealtime };
};
